/**
 * The Notes tab: `narrative_sections` grouped by note number, with the
 * note's tables inline where they share a page (§10.3).
 *
 * The note number comes from the *page*, not from the narrative row: this
 * database has `section_ordinal` and `ordinal_raw` NULL on all 4,603 rows,
 * while `document_pages.note_no` is populated on every note page. The tab
 * says which filing each version of a note came from, because a note is
 * restated every quarter and the differences are the interesting part.
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from 'wouter'
import type { CompanyNote, NoteTable } from '@server/sql/notes.ts'
import { ApiError, api, keys } from '~/lib/api.ts'
import { useT } from '~/i18n/index.tsx'
import { Panel, Skeleton } from '~/components/ui/primitives.tsx'
import { Disclosure } from '~/components/ui/disclosure.tsx'
import { EmptyState, ErrorState } from '~/components/ui/empty.tsx'
import { Raw, RawNumber } from '~/components/ui/raw.tsx'
import { cn } from '~/lib/cn.ts'

export function CompanyNotes({ symbol }: { symbol: string }) {
  const { t, tn } = useT()
  const { data, error, refetch } = useQuery({
    queryKey: keys.companyNotes(symbol, ''),
    queryFn: () => api<{ notes: CompanyNote[] }>(`/api/companies/${encodeURIComponent(symbol)}/notes`),
  })

  if (error) {
    const e = error as ApiError
    return <ErrorState title={t('common.error')} detail={e.message} requestId={e.requestId}
      onRetry={() => void refetch()} retryLabel={t('action.retry')} />
  }
  if (!data) return <Skeleton className="h-96 w-full" />
  if (data.notes.length === 0) return <EmptyState title={t('empty.generic')} />

  return (
    <div className="space-y-2">
      {data.notes.map((note) => (
        <Panel key={String(note.noteNo)} className="px-3 py-2">
          <Disclosure
            tone="strong"
            defaultOpen={data.notes.length <= 3}
            count={note.sections.length}
            summary={
              <span className="flex items-baseline gap-2">
                <span
                  title={t('notes.numberFrom')}
                  className="tnum font-[family-name:var(--font-mono)] text-[var(--text-muted)]"
                >
                  {note.noteNo === null ? '—' : String(note.noteNo).padStart(2, '0')}
                </span>
                <span>
                  {note.sections[0]?.headingRaw ? (
                    <Raw lang={note.sections[0].lang}>{note.sections[0].headingRaw}</Raw>
                  ) : (
                    tn('common.rows', note.sections.length, { n: note.sections.length })
                  )}
                </span>
              </span>
            }
          >
            <div className="space-y-2 ps-4 pt-1">
              {note.sections.map((s) => (
                <div key={s.id} className="border-s border-[var(--border)] ps-3">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <Link
                      href={`/c/${symbol}/d/${s.documentId}${s.pageNo ? `?p=${s.pageNo}` : ''}`}
                      className="tnum text-[length:var(--text-micro)] link-inline"
                    >
                      {s.period?.toUpperCase()} {s.fiscalYear}
                      {s.pageNo && ` · ${t('fin.goToPage', { n: s.pageNo })}`}
                    </Link>
                    {s.headingRaw && (
                      <Raw lang={s.lang} className="text-[length:var(--text-data)] font-medium">
                        {s.headingRaw}
                      </Raw>
                    )}
                  </div>
                  {s.bodyRaw && (
                    <Raw
                      as="p"
                      lang={s.lang}
                      className="mt-1 max-w-[92ch] text-[length:var(--text-prose)] leading-[var(--text-prose--line-height)] text-[var(--text)]"
                    >
                      {s.bodyRaw}
                    </Raw>
                  )}
                  {s.tables.map((tb) => <NoteTableView key={tb.id} table={tb} />)}
                </div>
              ))}
            </div>
          </Disclosure>
        </Panel>
      ))}
    </div>
  )
}

/**
 * A note's table. Simpler than the viewer's -- no per-cell audit, no digit
 * toggle -- because here it is being read, not verified.
 */
function NoteTableView({ table }: { table: NoteTable }) {
  const rtl = table.lang === 'ar'
  return (
    <div className="mt-2 overflow-x-auto rounded-[var(--radius-control)] border border-[var(--border)]">
      <table
        className="w-max min-w-full border-collapse text-[length:var(--text-tiny)]"
        dir={rtl ? 'rtl' : 'ltr'}
      >
        {table.titleRaw && (
          <caption className="border-b border-[var(--border)] bg-[var(--bg-sunken)] px-2 py-1 text-start">
            <Raw lang={table.lang} className="font-medium text-[var(--text)]">{table.titleRaw}</Raw>
          </caption>
        )}
        <thead>
          <tr>
            <th className="min-w-[12rem] border-b border-[var(--border)] px-2 py-1 text-start" />
            {table.columns.map((c) => (
              <th
                key={c.idx}
                className="whitespace-nowrap border-b border-s border-[var(--border)] px-2 py-1 text-end font-medium"
              >
                <Raw lang={table.lang}>{c.labelRaw ?? `#${c.idx + 1}`}</Raw>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((r) => (
            <tr key={r.order} className={cn('border-b border-[var(--border)] last:border-b-0', r.isTotal && 'bg-[var(--bg-sunken)]/60')}>
              <th
                scope="row"
                className={cn('px-2 py-1 text-start align-top font-normal', r.isTotal && 'font-semibold')}
                style={{ paddingInlineStart: 8 + (r.level ?? 0) * 10 }}
              >
                <Raw lang={table.lang}>{r.labelRaw ?? ''}</Raw>
              </th>
              {table.columns.map((c) => (
                <td
                  key={c.idx}
                  className={cn('whitespace-nowrap border-s border-[var(--border)] px-2 py-1 text-end', r.isTotal && 'font-semibold')}
                >
                  <RawNumber value={r.values[c.idx]?.raw ?? null} lang={table.lang} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

